import axios from 'axios';
import Vue from 'vue';
import {
  Datasets as datasetLibrary,
  siteGroupingsAPI,
  newReportsMapping,
} from '../centralBedfordshire_dataset_library';

const state = {
  datasets: {},
  datasetList: [],
  datesBySite: {},
  monthsBySite: {},
  datesByGroup: {},
  monthsByGroup: {},
  currentDataset: null,
  data: [],
  userGUID: null,
  organisationGUID: null,
  locations: [],
  currentLocation: '',
  datasetInput: [],
  reportInput: [],
  locationInput: [],
  dateInput: [],
  legendNodes: [],
  visibleNodes: [],
  selectedNode: [],
  reportList: [],
  s3Links: [],
  comparisonSwitch: false,
  currentReport: '',
  generatedReportMsg: null,
  generatedReportStatus: null,
  loading: false,
  fileUploadRes: null,
  authenticated: false,
  flaggedData: [],
  groupingData: [],
  chosenDistrict: '',
  chosenTown: '',
  // chosenProject: '',
};

const headers = {
  Authorization: null,
};

const getters = {
  checkAuth: (state) => {
    return state.authenticated;
  },
  datasetList: (state) => {
    return state.datasetList.map((key) => {
      return state.datasets[key];
    });
  },
  currentDataset: (state) => {
    return state.currentDataset ? state.datasets[state.currentDataset] : null;
  },
  s3Links: (state) => {
    return state.s3Links.map((link) => {
      const split = link.split('/');
      return { label: split[split.length - 1], href: link };
    });
  },
  locationList: (state) =>
    state.locations.sort((a, b) => {
      return a - b;
    }),
  currentLocation: (state) => state.currentLocation,
  getLegendNodes: (state) => state.legendNodes,
  reportList: (state) => {
    if (state.comparisonSwitch) {
      const allowed = [
        'Speed Reports',
        'Vehicle Report',
        'Multiday Volume Reports',
        'Multiday Speed Reports',
        'Emissions Reports',
        'Length Reports',
      ];
      return state.reportList.filter((name) => allowed.indexOf(name) > -1);
    }
    return state.reportList;
  },
  currentReport: (state) => state.currentReport,
  generatedReportRes: (state) => state.generatedReportMsg,
  generatedReportStatus: (state) => state.generatedReportStatus,
  getLoadStatus: (state) => state.loading,
  getFileUploadStatus: (state) => {
    return state.fileUploadRes;
  },
  restrictedDates: (state) => {
    if (
      Object.keys(state.datesBySite).length > 0 &&
      state.currentLocation !== ''
    ) {
      return state.datesBySite[state.currentLocation];
    }
    if (Object.keys(state.datesByGroup).length > 0 && state.chosenTown !== '') {
      return state.datesByGroup[state.chosenTown];
    }
    return [];
  },
  restrictedMonths: (state) => {
    if (
      Object.keys(state.monthsBySite).length > 0 &&
      state.currentLocation !== ''
    ) {
      return state.monthsBySite[state.currentLocation];
    }
    if (
      Object.keys(state.monthsByGroup).length > 0 &&
      state.chosenTown !== ''
    ) {
      return state.monthsByGroup[state.chosenTown];
    }
    return [];
  },
  dataTimeRange: (state) => {
    if (
      state.currentLocation !== '' &&
      state.datesBySite[state.currentLocation]
    ) {
      const dates = state.datesBySite[state.currentLocation];
      return { start: dates[0], end: dates[dates.length - 1] };
    }
    if (state.chosenTown && state.datesByGroup[state.chosenTown]) {
      const dates = state.datesByGroup[state.chosenTown];
      return { start: dates[0], end: dates[dates.length - 1] };
    }
    return { start: null, end: null };
  },
  getDistricts: (state) => {
    const districts = [
      ...new Set(state.groupingData.map((site) => site.District)),
    ]
      .filter((district) => district !== '')
      .sort();
    return districts;
  },
  getTowns: (state) => {
    const districts = [
      ...new Set(
        state.groupingData
          .filter((site) => site.District === state.chosenDistrict)
          .map((site) => site['Town/City'])
          .sort(),
      ),
    ];
    return districts;
  },
  getProjects: (state) => {
    const projects = [
      ...new Set(
        state.groupingData
          .filter(
            (site) =>
              site.District === state.chosenDistrict &&
              site['Town/City'] === state.chosenTown,
          )
          .map((site) => site['Area/Project']),
      ),
    ];
    return projects;
  },
  getGroupFilteredSites: (state) => {
    const sites = [
      ...new Set(
        state.groupingData
          .filter(
            (site) =>
              site.District === state.chosenDistrict &&
              site['Town/City'] === state.chosenTown,
          )
          .map((site) => site.Id),
      ),
    ];
    return sites;
  },
};

const actions = {
  async getFlaggedData(context, site) {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_VALIDATION_URL}/get_flag_by_site_id?`,
        {
          params: {
            site_id: site,
            datasetGUID: state.datasets[state.currentDataset].datasetGUID,
          },
          withCredentials: true,
        },
      );
      context.commit('SET_FLAGGED_DATA', response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchGroupingDataset(context) {
    try {
      const response = await axios.get(
        `${siteGroupingsAPI}?limit(-1)&cache=true`,
        {
          headers,
        },
      );
      const { data } = response;
      context.commit('SET_GROUPING_DATA', data);
    } catch (e) {
      console.error(e);
    }
  },
  async uploadFile(context, file) {
    context.commit('loading', true);
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_DASHBOARD_URL}s3generateSpecialPutRequest`,
        null,
        { params: file, headers },
      );
      const signedUrl = response.data.result.signedRequest;
      const options = {
        headers: {
          'Content-Type': response.data.result.contentType,
        },
      };

      const s3Res = await axios.put(signedUrl, file.data, options);
      context.commit('setFileUploadRes', s3Res.status);
    } catch (e) {
      console.error(e);
    }
  },
  async fetchS3Links(context) {
    context.commit('loading', false);
    try {
      const params = {
        prefix: '',
        organisationGUID: process.env.VUE_APP_ORG_ID,
      };
      if (context.state.currentLocation) {
        params.prefix = context.state.currentLocation;
      }
      const links = await axios.post(
        `${process.env.VUE_APP_DASHBOARD_URL}listS3Files`,
        null,
        { params, headers: { withCredentials: true } },
      );
      context.commit('storeS3Links', links.data.result);
    } catch (e) {
      console.error(e);
    }
  },
  async setCurrentUser(context, params) {
    context.commit('setUser', params);
  },
  async fetchData(context) {
    const { state } = context;
    const { apiResource } = state.datasets[state.currentDataset];
    const { siteIDField } = state.datasets[state.currentDataset];
    const query = `?limit(-1)&sort(+${siteIDField})&distinct(${siteIDField})`;
    try {
      const locations = await axios.get(`${apiResource + query}`, {
        headers,
      });
      context.commit('setLocations', locations);
    } catch (e) {
      context.commit('setLocations', {});
    }
  },
  async fetchRestrictedDates(context) {
    const { state } = context;
    const { index, primaryDateField } = state.datasets[state.currentDataset];
    const { organisationGUID, currentLocation, currentReport } = state;
    if (currentReport.length === 0) return;
    const report = currentReport.split(' ').join('');
    const params = {
      index,
      organisationGUID,
      field: primaryDateField,
      siteIDField: state.datasets[state.currentDataset].siteIDField,
    };
    if (state.datasets[state.currentDataset].type === 'new') {
      params.report = newReportsMapping[report];
    }
    if (state.chosenTown) {
      params.sites = context.getters.getGroupFilteredSites;
    } else {
      params.site = currentLocation;
    }
    const url = `${process.env.VUE_APP_REPORT_URL}reports:data_dates`;
    console.log('[fetchRestrictedDates] URL:', url);
    console.log('[fetchRestrictedDates] params:', JSON.stringify(params, null, 2));
    let values;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          authorization: context.state.accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      console.log('[fetchRestrictedDates] response status:', response.status, response.statusText);
      const responseText = await response.text();
      console.log('[fetchRestrictedDates] response body:', responseText);
      if (!response.ok) {
        console.error(`fetchRestrictedDates failed: ${response.status} ${response.statusText}`, responseText);
        return;
      }
      values = JSON.parse(responseText);
    } catch (error) {
      console.error('fetchRestrictedDates network error:', error);
      return;
    }
    context.commit('setRestrictedDates', values);
  },
  async setupLegendNodes(context, nodes) {
    const keyedNodes = Object.keys(nodes).map((key, index) => {
      return {
        label: nodes[key].label,
        description: nodes[key].description,
        color: nodes[key].color,
        ID: index,
        dataset: key,
      };
    });
    context.commit('setLegendNodes', keyedNodes);
  },
  async resetForm(context) {
    context.commit('resetState');
  },
  async fetchComposite(context) {
    // loop datasets and add index as id to the list
    const datasetList = [];
    const datasets = [];
    datasetLibrary.forEach((dataset, index) => {
      datasets.push(dataset);
      datasetList.push(index);
    });
    const payload = {
      datasetList,
      datasets,
    };
    context.commit('setCompositeDataset', payload);
  },
  async generateReport(context, data) {
    context.commit('loading', true);
    context.commit('clearGeneratedReportRes');
    // const reportService = axios.create({
    //   withCredentials: true,
    //   baseURL: process.env.VUE_APP_REPORT_URL,
    //   timeout: 1000,
    //   headers: data.headers
    // });
    const { state } = context;
    let valid = true;
    if (
      !state.datasets[state.currentDataset] ||
      !state.datasets[state.currentDataset].datasetGUID
    ) {
      context.commit('loading', false);
      context.commit('setValidation', {
        input: 'datasetInput',
        err: 'Requires a dataset to be selected',
      });
      valid = false;
    }
    if (!state.currentReport) {
      context.commit('loading', false);
      context.commit('setValidation', {
        input: 'reportInput',
        err: 'Requires a report to be selected',
      });
      valid = false;
    }
    if (
      state.chosenTown === '' &&
      (!state.currentLocation || state.currentLocation === '')
    ) {
      context.commit('loading', false);
      context.commit('setValidation', {
        input: 'locationInput',
        err: 'Requires a location range to be selected',
      });
      valid = false;
    }
    if (data.dates.length < 2 || data.dates.includes(null)) {
      context.commit('loading', false);
      context.commit('setValidation', {
        input: 'dateInput',
        err: 'Requires a date range to be selected',
      });
      valid = false;
    }
    if (
      data.isComparison &&
      (data.dates.length < 2 || data.comparisonDates.length < 2)
    ) {
      context.commit('loading', false);
      context.commit('setValidation', {
        input: 'dateInput',
        err: 'Requires two date ranges for a comparison report to be generated',
      });
      valid = false;
    }
    if (!valid) return valid;
    const params = {
      datasetGUID: state.datasets[state.currentDataset].datasetGUID,
      // userGUID: state.userGUID,
      organisationGUID: state.organisationGUID,
      startDate: null,
      endDate: null,
      // siteNumber: state.currentLocation,
      dataset_type: state.datasets[state.currentDataset].title,
    };
    function isBefore(start, end) {
      return start < end;
    }
    if (data.dates.length > 0) {
      const [startDate, endDate] = data.dates;

      params.startDate = isBefore(startDate, endDate) ? startDate : endDate;
      params.endDate = isBefore(startDate, endDate) ? endDate : startDate;
    }
    if (data.isComparison) {
      const [startDate1, endDate1] = data.comparisonDates;

      params.startDate1 = isBefore(startDate1, endDate1)
        ? startDate1
        : endDate1;
      params.endDate1 = isBefore(startDate1, endDate1) ? endDate1 : startDate1;
    }
    if (state.chosenTown) {
      params.sites = context.getters.getGroupFilteredSites;
    } else {
      params.siteNumber = state.currentLocation;
    }
    const report = state.currentReport.split(' ').join('');
    const baseURL = process.env.VUE_APP_REPORT_URL;
    let url = '';
    if (state.datasets[state.currentDataset].type === 'new') {
      url = state.datasets[state.currentDataset].reportUrl;
      params.type = newReportsMapping[report];
    } else {
      url = data.isComparison
        ? `${baseURL}${
            state.datasets[state.currentDataset].comparisonReports[report]
          }`
        : `${baseURL}${state.datasets[state.currentDataset][report]}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          authorization: context.state.accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      const responseData = await response.text();
      if (!response.ok) {
        context.commit('setGeneratedReportResp', {
          err: { response: { data: responseData } },
          status: response.status,
        });
      } else {
        context.commit('setGeneratedReportResp', { data: responseData, status: response.status });
      }
    } catch (err) {
      console.error('generateReport network error:', err);
      context.commit('setGeneratedReportResp', { err: { response: { data: err.message } }, status: 500 });
    } finally {
      context.commit('loading', false);
    }
    return data;
  },
  async generatePreview(context, data) {
    context.commit('loading', true);
    context.commit('clearGeneratedReportRes');
    const { state } = context;
    const report = state.currentReport.split(' ').join('');
    const baseURL = process.env.VUE_APP_REPORT_URL;
    let url = null;
    const params = {
      datasetGUID: state.datasets[state.currentDataset].datasetGUID,
      // userGUID: state.userGUID,
      organisationGUID: state.organisationGUID,
      startDate: null,
      endDate: null,
      siteNumber: state.currentLocation,
      dataset_type: state.datasets[state.currentDataset].title,
      preview: true,
    };
    if (state.datasets[state.currentDataset].type === 'new') {
      url = state.datasets[state.currentDataset].reportUrl;
      params.type = newReportsMapping[report];
    } else {
      url = data.isComparison
        ? `${baseURL}${
            state.datasets[state.currentDataset].comparisonReports[report]
          }`
        : `${baseURL}${state.datasets[state.currentDataset][report]}`;
    }
    function isBefore(start, end) {
      return start < end;
    }
    if (data.dates.length > 0) {
      const [startDate, endDate] = data.dates;

      params.startDate = isBefore(startDate, endDate) ? startDate : endDate;
      params.endDate = isBefore(startDate, endDate) ? endDate : startDate;
    }
    if (data.isComparison) {
      const [startDate1, endDate1] = data.comparisonDates;

      params.startDate1 = isBefore(startDate1, endDate1)
        ? startDate1
        : endDate1;
      params.endDate1 = isBefore(startDate1, endDate1) ? endDate1 : startDate1;
    }
    let result;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          authorization: context.state.accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        const errData = await response.text();
        context.commit('setGeneratedReportResp', {
          err: { response: { data: errData } },
          status: response.status,
        });
        return { err: errData, status: response.status };
      }
      if (state.datasets[state.currentDataset].type === 'new') {
        const responseData = await response.json();
        result = { data: responseData, format: 'new' };
        if (!result.data.html) {
          context.commit('setGeneratedReportResp', result);
        } else {
          context.commit('clearGeneratedReportRes');
        }
      } else {
        const res = await response.json();
        const csvResponse = await fetch(res.csv);
        if (!csvResponse.ok) {
          const errData = await csvResponse.text();
          context.commit('setGeneratedReportResp', {
            err: { response: { data: errData } },
            status: csvResponse.status,
          });
          return { err: errData, status: csvResponse.status };
        }
        const blob = await csvResponse.blob();
        result = { data: blob };
        context.commit('clearGeneratedReportRes');
      }
    } catch (err) {
      console.error('generatePreview network error:', err);
      context.commit('setGeneratedReportResp', {
        err: { response: { data: err.message } },
        status: 500,
      });
      result = err;
    } finally {
      context.commit('loading', false);
    }
    return result;
  },
  updateCurrentLocation(context, location) {
    const { state } = context;
    if (state.locations.indexOf(location) !== -1) {
      context.commit('updateLocation', location);
    }
  },
  fetchAvailableReports(context) {
    const { state } = context;
    context.commit('updateReports', state.currentDataset);
  },
  toggleLegendNodes(context) {
    context.commit('toggleLegendNodes');
  },
};

const mutations = {
  SET_DISTRICT_FILTER(state, payload) {
    state.chosenDistrict = payload;
  },
  SET_TOWN_FILTER(state, payload) {
    state.chosenTown = payload;
  },
  // SET_PROJECT_FILTER(state, payload) {
  //   state.chosenProject = payload;
  // },
  SET_GROUPING_DATA(state, payload) {
    state.groupingData = payload;
  },
  SET_AUTH(state, payload) {
    state.accessToken = payload.accessToken;
    headers.Authorization = payload.accessToken;
    state.authenticated = true;
  },
  SET_UNAUTHENTICATED(state) {
    state.accessToken = null;
    headers.Authorization = null;
    state.authenticated = false;
  },
  SET_FLAGGED_DATA(state, payload) {
    if (payload.length > 0) {
      state.flaggedData = payload;
    }
  },
  setCompositeDataset(state, payload) {
    payload.datasetList.forEach((id) => {
      state.datasetList.push(id);
      const dataset = payload.datasets[id];
      Vue.set(state.datasets, id, dataset);
    });
  },
  setComparisonSwitch(state, payload) {
    state.comparisonSwitch = payload.isComparison;
  },
  setUser(state, payload) {
    // state.userGUID = payload.userGUID;
    state.organisationGUID = payload.organisationGUID;
  },
  setRestrictedDates(state, payload) {
    // array of dates for selected site
    state.datesBySite = {};
    if (payload.length > 0) {
      const formattedDates = payload.map((date) => date.substring(0, 10));
      const formattedMonths = payload.map((date) => date.substring(0, 7));
      if (state.currentLocation && state.currentLocation !== '') {
        Vue.set(state.datesBySite, state.currentLocation, formattedDates);
        Vue.set(state.monthsBySite, state.currentLocation, formattedMonths);
      } else if (state.chosenTown && state.chosenTown !== '') {
        Vue.set(state.datesByGroup, state.chosenTown, formattedDates);
        Vue.set(state.monthsByGroup, state.chosenTown, formattedMonths);
      }
    }
  },
  updateReports(state) {
    state.currentReport = '';
    state.currentLocation = '';
    state.reportList = state.currentDataset
      ? state.datasets[state.currentDataset].availableReports
      : [];
  },
  resetState(state) {
    // state.currentDataset = null;
    state.datesBySite = {};
    state.datesByGroup = {};
    state.currentLocation = '';
    state.currentReport = '';
    state.chosenDistrict = '';
    state.chosenTown = '';
    // state.chosenProject = '';
    // state.locationInput.pop();
    // state.reportInput.pop();
    // state.datasetInput.pop();
    state.selectedNode = [];
    state.locations = [];
    // state.visibleNodes = state.legendNodes.map((node) => node.ID);
  },
  resetValidation(state, name) {
    switch (name) {
      case 'currentDataset':
        state.datasetInput.pop();
        break;
      case 'currentReport':
        state.reportInput.pop();
        break;
      case 'currentLocation':
        state.locationInput.pop();
        break;
      default:
        state.datasetInput.pop();
        state.reportInput.pop();
        state.locationInput.pop();
    }
  },
  setLocations(state, payload) {
    if (payload.data) {
      const unique = Array.from(
        new Set(payload.data.map((v) => v.key.toString())),
      );
      state.locations = unique.sort((a, b) => a - b);
    } else {
      state.locations = [];
    }
  },
  updateLocation(state, payload) {
    state.currentLocation = payload;
  },
  setLegendNodes(state, payload) {
    payload.forEach((node) => {
      state.visibleNodes.push(node.ID);
    });
    state.legendNodes = payload;
  },
  setGeneratedReportResp(state, res) {
    if (!res.err) {
      state.generatedReportMsg = res.data;
      state.generatedReportStatus = res.status;
    } else {
      state.generatedReportMsg = res.err.response.data;
      state.generatedReportStatus = res.status;
    }
  },
  setValidation(state, res) {
    if (state[res.input].length === 0) {
      state[res.input].push(res.err);
    }
  },
  clearGeneratedReportRes(state) {
    state.generatedReportMsg = null;
    state.generatedReportStatus = null;
  },
  loading(state, type) {
    state.loading = type;
  },
  setFileUploadRes(state, status) {
    state.fileUploadRes = status;
    state.loading = false;
  },
  setVisibleNodes(state, node) {
    if (node !== null && node !== undefined) {
      const index = state.visibleNodes.indexOf(node);
      if (index !== -1) {
        state.visibleNodes.splice(index, 1);
      } else {
        state.visibleNodes.push(node);
      }
    } else if (state.currentDataset) {
      const selectedNode = state.legendNodes
        .filter((value) => {
          return value.dataset === state.datasets[state.currentDataset].title;
        })
        .map((v) => {
          return v.ID;
        })[0];
      state.visibleNodes.splice(0, state.visibleNodes.length);
      state.visibleNodes.push(selectedNode);
    }
  },
  toggleLegendNodes(state) {
    if (state.visibleNodes.length > 0) {
      state.visibleNodes = [];
    } else {
      state.visibleNodes = state.legendNodes.map((v) => v.ID);
    }
  },
  setState(state, obj) {
    if (obj.name === 'currentDataset') {
      Object.keys(state.datasets).forEach((key) => {
        if (obj.value === state.datasets[key].title) {
          state[obj.name] = key;
        }
      });
    } else {
      state[obj.name] = obj.value;
    }
  },
  storeS3Links(state, result) {
    state.s3Links = result;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
